import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { Helmet } from 'react-helmet'
import useCompanyData from '../hooks/useCompanyData'
import { Button, InlineAlert, Input, LoadingSpinner, SearchableSelect } from '../components/BaseComponents'
import {
	default as ISICSelector,
	validate as ISICValidation,
} from '../components/BaseComponents/ISICSelector/ISICSelector'
import { currencyToCents, getCSRFToken } from '../lib/utils'
import CurrencyInput from '../components/BaseComponents/Forms/CurrencyInput'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useUserData from '../hooks/useUserData'
import { routes } from '../components/Router/routes'
import { CurrencyCode } from '../types/currency'
import { resolveCurrencyCode } from '../lib/currency'
import useApi from '../hooks/useApi'

type CompanyRequest = {
	id: string
	turnover: number
	number_of_employees?: number
	industryCodes: string[]
	currencyCode: string
}

const currencyCodeOptions: { value: CurrencyCode, label: string }[] = [
	{ value: 'GBP', label: 'GBP' },
	{ value: 'EUR', label: 'EUR' },
	{ value: 'USD', label: 'USD' },
]

function CompanySetup() {
	const userData = useUserData()
	const companyId = userData.companyId
	const navigate = useNavigate()
	const { companyData, error, isLoading, refetchCompanyData } = useCompanyData(companyId)
	const [submissionError, setSubmissionError] = useState(false)
	const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('EUR');
	const api = useApi();

	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		reset,
	} = useForm({ mode: 'onSubmit' })

	const initialised = useRef(false)
	useEffect(() => {
		if (companyData && !initialised.current) {
			initialised.current = true
			setCurrencyCode(resolveCurrencyCode(companyData.currency_code))
			reset({
				turnover: companyData.turnover,
				industryCodes: companyData.industry_codes,
				number_of_employees: companyData.number_of_employees,
			})
		}
	}, [companyData, reset])

	const onSubmit: SubmitHandler<any> = async (data: CompanyRequest) => {
		let csrfToken = await getCSRFToken()

		// The frontend always send type: ISIC align with the code for now, since we don’t use any other type.
		const isic = data.industryCodes.map((code) => ({ codeType: 'ISIC', code }))

		// Convert turnover value if it has changed
		let turnover = companyData?.turnover
		if (companyData?.turnover !== data.turnover) {
			turnover = currencyToCents(data.turnover)
		}

		const dataWithCsrf = {
			...data,
			industryCodes: isic,
			turnover,
			currencyCode,
			_csrf: csrfToken,
		}

		try {
			await api.post(`company/setUp/${companyId}`, dataWithCsrf)
			setSubmissionError(false)
			navigate(routes.landingPage.path, { replace: true })
		} catch(error) {
			// Display submission error box
			setSubmissionError(true)
		}
	}

	return (
		<div data-testid='company-setup-main' className='w-full'>
			<Helmet>
				<title>{routes.companySetup.name}</title>
			</Helmet>
			{isLoading && <LoadingSpinner />}
			{error && (
				<div className='flex h-dvh flex-col justify-center items-center text-white text-2xl'>
					<p>{error}</p>
					<Button type='Primary' text='Retry organisation' onClick={refetchCompanyData} />
				</div>
			)}
			{companyData && (
				<form className='flex justify-center items-center h-dvh flex-col'>
					<h2 className='text-2xl text-charcoal-050'>Review Organisation details</h2>

					<p className='sr-only'>* denotes a required field</p>

					<div className='my-3xl p-lg bg-charcoal-050 rounded-md w-2/3 lg:w-1/3 grid'>
						<div className='grid grid-cols-2 my-2'>
							<span>{companyData.name}</span>
						</div>

						<hr className='text-charcoal-200 mt-sm' />

						<div className="flex mt-sm">
							<div className='text-charcoal-500'>Jurisdiction:</div>
							<div className="grow text-right">{companyData.jurisdiction_code}</div>
						</div>
						<div className="flex mt-sm">
							<div className='text-charcoal-500'>Organization Number:</div>
							<div className="grow text-right">{companyData.number}</div>
						</div>

						<hr className='text-charcoal-200 mt-sm'></hr>

						<div className='grid grid-cols-2 my-2'>
							<p>
								<span className='text-charcoal-500'>Currency</span>
								<span className='text-error-600'> *</span>
								<span className='sr-only'>(required)</span>
							</p>

							<SearchableSelect
								isSearchable={false}							isClearable={false}								options={currencyCodeOptions}
								value={currencyCodeOptions.find((option) => option.value === currencyCode)}
								onChange={(option) => option && setCurrencyCode(option.value)}
							/>
						</div>
						<div className='grid grid-cols-2 my-2'>
							<p>
								<span className='text-charcoal-500'>Average yearly turnover</span>
								<span className='text-error-600'> *</span>
								<span className='sr-only'>(required)</span>
							</p>

							<Controller
								name='turnover'
								control={control}
								defaultValue={companyData.turnover}
								rules={{ required: true }}
								render={({ field: { onChange, value } }) => (
									<>
										<CurrencyInput
											name='turnover'
											errors={errors}
											placeholder={'0,000,000'}
											defaultValue={companyData.turnover}
											onChange={onChange}
											currencyCode={currencyCode}
										/>
										Value: { JSON.stringify(value) }
									</>
								)}
							/>
						</div>
						<hr className='text-charcoal-200 my-sm'></hr>

						<div className='grid grid-cols-2 my-2'>
							<p className='text-charcoal-500'>
								Number of employees
								<span className='block text-xs'>Full time equivalent</span>
							</p>

							<Input
								register={register}
								errors={errors}
								type='number'
								name='numberOfEmployees'
								validation={{ min: 0 }}
								defaultValue={companyData.number_of_employees && companyData.number_of_employees}
							/>
						</div>

						<div className='my-2'>
							<p>
								<span className='text-charcoal-500'>Nature of business: Sector Code (ISIC)</span>
								<span className='text-error-600'> *</span>
								<span className='sr-only'>(required)</span>
							</p>
							<Controller
								name='industryCodes'
								control={control}
								defaultValue={(companyData && companyData.industry_codes) || []}
								rules={{
									validate: {
										minmax: ISICValidation(1, 4, (companyData && companyData.industry_codes) || []),
									},
								}}
								render={({ field: { onChange }, fieldState: { error, isDirty } }) => (
									<ISICSelector
										updateHandler={onChange}
										error={error}
										isDirty={isDirty || !!error}
										fixedValues={(companyData && companyData.industry_codes) || []}
									/>
								)}
							/>
						</div>
						<hr className='text-charcoal-200 my-sm'></hr>

						<p className='text-charcoal-500 mt-2'>Registered address</p>
						<p className='text-sm text-charcoal-900 mt-3'>{companyData.address}</p>
						<hr className='text-charcoal-200 my-sm'></hr>
					</div>

					{submissionError && (
						<InlineAlert type='error' className='my-3xl w-2/3 lg:w-1/3'>
                                                        Oops! There was an error submitting your data. Please try again: if the problem persists, please contact the Neoni team.
                                                </InlineAlert>
					)}

					<Button
						onClick={handleSubmit(onSubmit)}
						type='Primary'
						hasTrailingIcon={true}
						icon='fa-solid fa-chevron-right'
						text='Finish Setup'
					/>
				</form>
			)}
		</div>
	)
}
export default CompanySetup
